import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Divider, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { styled } from '@mui/material/styles';
import useSessionTimeline from 'api-new/stim-based/useSessionTimeline';
import HeatmapTimelineResult from './HeatmapTimelineResult';
import { PollActivityResultView, TextPromptActivityResultView, RankingActivityResultView, AgreementActivityResultView } from '@visionslive/reporting-components';

const formatTime = (timestamp) => {
    const d = new Date(timestamp);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const TimestampCell = styled(TableCell)({
    whiteSpace: 'nowrap',
    color: '#627D98',
    fontSize: '0.8rem',
    verticalAlign: 'top',
    paddingTop: 6,
    width: 80,
    minWidth: 80
});

const EventBanner = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 4,
    backgroundColor: theme.palette.grey[100],
    borderLeft: `4px solid ${theme.palette.divider}`
}));

const ChatMessageEvent = ({ event }) => (
    <TableRow>
        <TimestampCell>{formatTime(event.timestamp)}</TimestampCell>
        <TableCell sx={{ verticalAlign: 'top', width: 140, minWidth: 140, borderLeft: `4px solid ${event.senderColor || '#9e9e9e'}`, paddingBottom: 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                {event.senderName}
            </Typography>
            {event.moderatorTag && (
                <Chip label={event.moderatorTag} size='small' variant='outlined' sx={{ mt: 0.5, fontSize: '0.7rem', height: 18 }} />
            )}
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }}>
            <Typography sx={{ fontSize: '0.875rem' }}>
                {event.message}
            </Typography>
        </TableCell>
    </TableRow>
);

const StimulusEvent = ({ event }) => (
    <TableRow>
        <TimestampCell>{formatTime(event.timestamp)}</TimestampCell>
        <TableCell colSpan={2}>
            <EventBanner>
                <Typography variant='body2' sx={{ fontWeight: 600, color: '#5521B5' }}>
                    {event.type === 'StimulusStarted' ? 'Stimulus shown' : 'Stimulus ended'}
                </Typography>
                <Typography variant='body2'>{event.title}</Typography>
            </EventBanner>
            {event.type === 'StimulusStarted' && event.stimulusType === 'Image' && event.mediaUrl && (
                <Box sx={{ mt: 1, maxWidth: 400 }}>
                    <img src={event.mediaUrl} alt={event.title} style={{ width: '100%', borderRadius: 4, display: 'block' }} />
                </Box>
            )}
            {event.type === 'StimulusStarted' && event.stimulusType === 'Text' && event.textValue && (
                <Box sx={{ mt: 1, p: 1.5, backgroundColor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>{event.textValue}</Typography>
                </Box>
            )}
        </TableCell>
    </TableRow>
);

const ActivityEvent = ({ event }) => {
    const { t } = useTranslation();
    const isStopped = event.type === 'ActivityStopped';
    const label = isStopped ? 'Activity ended' : 'Activity started';

    // PollActivityResultView expects per-respondent responses.
    const pollOptions = event.pollResults?.options?.map(o => ({ textValue: o.text, sortOrder: null, createdDate: null })) ?? [];
    const pollResponses = event.pollResults?.answers?.map(a => ({
        id: a.id,
        value: a.value,
        respondentId: a.respondentId,
        respondentName: a.respondentName,
        respondentColor: a.respondentColor,
        sessionId: 'session'
    })) ?? [];

    // AgreementActivityResultView expects {statements: {id→stmt}, answers: [{statementId, score}], options: [{id, score, label}]}.
    // API returns aggregated responseCount per option; synthesize the expected shapes.
    const agreementStmts = event.agreementResults?.statements ?? [];
    const agreementStatements = Object.fromEntries(
        agreementStmts.map((s, i) => [String(i), { id: String(i), statementText: s.statementText }])
    );
    const agreementOptions = (agreementStmts[0]?.options ?? []).map((o, i) => ({ id: String(i), score: o.score, label: t(o.label, { defaultValue: o.label }) }));
    const agreementAnswers = agreementStmts.flatMap((s, i) =>
        (s.options ?? []).flatMap((o, j) =>
            Array.from({ length: o.responseCount ?? 0 }, (_, k) => ({
                id: `${i}-${j}-${k}`, statementId: String(i), score: o.score, respondentName: null, respondentColor: null
            }))
        )
    );

    return (
        <TableRow>
            <TimestampCell>{formatTime(event.timestamp)}</TimestampCell>
            <TableCell colSpan={2}>
                <EventBanner sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant='body2' sx={{ fontWeight: 600, color: isStopped ? '#0E7C3A' : '#0A558C' }}>
                            {label}
                        </Typography>
                        <Chip label={event.activityType} size='small' variant='outlined' sx={{ fontSize: '0.7rem', height: 18 }} />
                        <Typography variant='body2'>{event.title}</Typography>
                    </Box>
                    {event.prompt && (
                        <Typography variant='body2' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            {event.prompt}
                        </Typography>
                    )}
                </EventBanner>
                {isStopped && (
                    <Stack direction='column' gap={1} sx={{ mt: 1 }}>
                        {event.pollResults && (
                            <PollActivityResultView
                                options={pollOptions}
                                responses={pollResponses}
                                hideFilters
                                optimiseForPrint
                            />
                        )}
                        {event.textPromptAnswers?.length > 0 && (
                            <TextPromptActivityResultView
                                responses={event.textPromptAnswers.map(r => ({ textValue: r.answer ?? '', respondentName: r.respondentName, sessionTitle: '' }))}
                                hideFilters
                                optimiseForPrint
                            />
                        )}
                        {event.rankingResults && (
                            <RankingActivityResultView
                                options={event.rankingResults.options ?? []}
                                responses={event.rankingResults.responses ?? []}
                                hideFilters
                                optimiseForPrint
                            />
                        )}
                        {event.heatmapResults && <HeatmapTimelineResult heatmapResults={event.heatmapResults} />}
                        {event.agreementResults && (
                            <AgreementActivityResultView
                                statements={agreementStatements}
                                answers={agreementAnswers}
                                options={agreementOptions}
                                anonymise={true}
                                optimiseForPrint
                            />
                        )}
                    </Stack>
                )}
            </TableCell>
        </TableRow>
    );
};

const renderEvent = (event, index) => {
    switch (event.type) {
        case 'ChatMessage': return <ChatMessageEvent key={index} event={event} />;
        case 'StimulusStarted':
        case 'StimulusStopped': return <StimulusEvent key={index} event={event} />;
        case 'ActivityStarted':
        case 'ActivityStopped': return <ActivityEvent key={index} event={event} />;
        default: return null;
    }
};

const SessionReportTabPanel = ({ focusGroupId }) => {
    const { data: events, isLoading } = useSessionTimeline(focusGroupId);

    if (isLoading) {
        return <Box sx={{ p: 2 }}>Loading...</Box>;
    }

    if (!events?.length) {
        return <Box sx={{ p: 2 }}><Typography color='text.secondary'>No session data available.</Typography></Box>;
    }

    return (
        <div id='vl-session-report'>
            <Box sx={{ p: 2, maxWidth: 960, margin: '0 auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, '@media print': { display: 'none' } }}>
                    <Button variant='outlined' size='small' startIcon={<PrintIcon />} onClick={() => window.print()}>
                        Print / Save as PDF
                    </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Table size='small'>
                    <TableBody>
                        {events.map((event, index) => renderEvent(event, index))}
                    </TableBody>
                </Table>
            </Box>
        </div>
    );
};

SessionReportTabPanel.propTypes = {
    focusGroupId: PropTypes.string.isRequired
};

ChatMessageEvent.propTypes = { event: PropTypes.object };
StimulusEvent.propTypes = {
    event: PropTypes.shape({
        timestamp: PropTypes.string,
        type: PropTypes.string,
        title: PropTypes.string,
        stimulusType: PropTypes.string,
        mediaUrl: PropTypes.string,
        textValue: PropTypes.string
    })
};
ActivityEvent.propTypes = { event: PropTypes.object };

export default SessionReportTabPanel;
